import type { ComponentChildren } from "preact";
import { useRef } from "preact/hooks";
import { cx } from "../cx";

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedProps<T extends string> {
  value: T;
  options: readonly SegmentedOption<T>[];
  onChange: (value: T) => void;
  label?: ComponentChildren;
  description?: ComponentChildren;
  ariaLabel?: string;
  disabled?: boolean;
  class?: string;
}

/** Few-option picker (radio group). Left/Right change the value, Tab leaves the group. */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  description,
  ariaLabel,
  disabled = false,
  class: cls,
}: SegmentedProps<T>) {
  const group = useRef<HTMLDivElement>(null);

  const onKeyDown = (e: KeyboardEvent) => {
    const step = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (step === 0 || disabled) return;
    const i = options.findIndex((o) => o.value === value);
    const next = options[(i + step + options.length) % options.length];
    if (!next) return;
    e.preventDefault();
    onChange(next.value);
    // Move focus with the selection (roving tabindex).
    requestAnimationFrame(() => group.current?.querySelector<HTMLElement>('[aria-checked="true"]')?.focus());
  };

  const control = (
    <div
      ref={group}
      class="segmented__group"
      role="radiogroup"
      aria-label={label ? undefined : ariaLabel}
      onKeyDown={onKeyDown}
    >
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={selected}
            class={cx("segmented__opt", selected && "is-selected")}
            tabIndex={selected ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(o.value)}
            data-nav={selected ? "" : undefined}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );

  if (!label) return <div class={cx("segmented", cls)}>{control}</div>;
  return (
    <div class={cx("segmented", "segmented--row", disabled && "is-disabled", cls)}>
      <div class="segmented__text">
        <span class="segmented__label">{label}</span>
        {description && <span class="segmented__desc">{description}</span>}
      </div>
      {control}
    </div>
  );
}
